import React, { useState, useRef } from "react";
import { Box, Typography, Grid, Button, TextField } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import BodySkinMapMale from "../../../../../common/bodymap/bodyPartSelection";

const MaleBodyMap = ({ onIncidentSubmit, setUploadedImages }) => {
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [comment, setComment] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);

  // Start Recording
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunks.current = [];

    mediaRecorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioURL(audioUrl);
    };

    mediaRecorder.start();
    setRecording(true);
  };

  // Stop Recording
  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  // Handle Submit - Save Recorded Entry
  const handleSubmitRecord = async () => {
    // ✅ Prevent empty submissions
    if (!audioURL && !comment.trim()) {
      alert("Please provide a comment or an audio recording.");
      return;
    }

    try {
      let audioUrl = null;

      // ✅ Upload audio if available
      if (audioURL) {
        const response = await fetch(audioURL);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append("audio", blob, "recording.wav");

        const uploadRes = await fetch("/api/audio", {
          method: "POST",
          body: formData,
        });

        const result = await uploadRes.json();
        audioUrl = result.audioUrl;
      }

      // ✅ Save to parent via callback
      const newRecord = {
        behavior: comment ? "Comment" : "Recording",
        audio: audioUrl,
        comment: comment.trim(),
      };

      onIncidentSubmit(newRecord);
      setComment("");
      setAudioURL(null);
    } catch (err) {
      console.error("Audio upload or record submission failed:", err);
    }
  };

  const handleUploadedImages = (data) => {
    setUploadedImages(data);
  };

  return (
    <Grid container spacing={2}>
      <Grid item lg={6} md={6}>
        <BodySkinMapMale onUpload={handleUploadedImages} />
      </Grid>
      <Grid item lg={6} md={6}>
        <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold" }} mt={20}>
          Provide a description for the selected area
        </Typography>
        {/* Description */}
        <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
          Including approximate size and color.
        </Typography>

        {/* Audio Recording UI */}
        <Box
          sx={{
            border: "2px dashed #ccc",
            padding: 3,
            textAlign: "center",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 180,
            mb: 2,
          }}
        >
          {!audioURL ? (
            <>
              <Typography sx={{ color: "#757575", fontSize: "16px", mb: 1 }}>Try Recording Your Sound Now</Typography>
              <MicIcon sx={{ fontSize: 50, color: "#757575", mb: 1 }} />
              <Typography sx={{ fontSize: "14px", color: "#757575", mb: 1 }}>
                To start recording press the <b>“Start Recording”</b> button and start recording!
              </Typography>
              <Button variant='contained' color='primary' onClick={recording ? stopRecording : startRecording}>
                {recording ? "Stop Recording" : "Start Recording"}
              </Button>
            </>
          ) : (
            <>
              <audio controls src={audioURL} />
              <Button variant='contained' color='primary' sx={{ mt: 2 }} onClick={() => setAudioURL(null)}>
                Record Again
              </Button>
            </>
          )}
        </Box>
        {/* Notes Field */}
        <Typography sx={{ fontSize: "14px", mb: 1 }}>Comment</Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder='Enter your comment here...'
          variant='outlined'
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        {(audioURL || comment) && (
          <Button variant='contained' color='primary' sx={{ mt: 2 }} onClick={handleSubmitRecord}>
            Submit
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

export default MaleBodyMap;
